import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, Target, Lightbulb, Shield, Users } from 'lucide-react';

export const About = () => {
  const values = [
    {
      icon: Lightbulb,
      title: 'Relentless Curiosity',
      description: 'We constantly experiment with AI engines to understand what actually works, staying ahead of the curve.'
    },
    {
      icon: Users,
      title: 'Client-First',
      description: 'We recommend what we would do if it were our own business—no upselling, just honest advice.'
    },
    {
      icon: Shield,
      title: 'Transparency',
      description: 'No black boxes. You\'ll see what we\'re doing, why we\'re doing it, and the results we\'re getting.'
    },
    {
      icon: Target,
      title: 'Long-Term Thinking',
      description: 'We build sustainable AI authority, not short-term tricks that could harm your brand.'
    }
  ];

  const team = [
    {
      name: 'Julien',
      role: 'Chief Marketing Officer',
      bio: 'Leads Evolution GEO’s go-to-market strategy. 10+ years in digital marketing and ecommerce growth, from performance campaigns to brand positioning.'
    },
    {
      name: 'Joseph',
      role: 'Automation Engineer & Developer',
      bio: 'Designs and builds the automations behind GEO/AIO—content pipelines, lead capture flows, reporting, and integrations with your existing tools.'
    },
    {
      name: 'Cyril',
      role: 'SEO & GEO/AIO Strategist',
      bio: 'Owns the search and AI-visibility strategy. Combines technical SEO, content architecture, and authority building so AI engines learn to trust your brand.'
    },
    {
      name: 'Olga',
      role: 'Head of Sales & Partnerships',
      bio: 'Leads client relationships and partnerships. Helps translate GEO/AIO and automation roadmaps into clear engagement models and outcomes for your team.'
    }
  ];

  return (
    <div className="min-h-screen pb-20">
      {/* Header */}
      <section className="pt-24 lg:pt-32 pb-12 lg:pb-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold mb-6">
            About <span className="text-gradient">Evolution GEO</span>
          </h1>
          <p className="text-lg sm:text-xl text-muted-foreground max-w-3xl mx-auto">
            We're on a mission to help future-proof brands in the age of AI search and AI agents.
          </p>
        </div>
      </section>

      {/* Story */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            <p className="text-lg text-foreground leading-relaxed mb-6">
              AI search is changing how people discover and choose brands. Every day, millions turn to ChatGPT, Perplexity, Claude, and Google's AI Overviews instead of traditional search engines. But here's the problem: if your brand isn't being cited, recommended, or referenced in those AI answers, you're invisible.
            </p>
            <p className="text-lg text-foreground leading-relaxed mb-6">
              We built Evolution GEO to help companies move beyond traditional SEO and become the trusted sources AI engines rely on. We blend deep experience in SEO, content, data, and AI technologies with a pragmatic, results-driven approach.
            </p>
            <p className="text-lg text-foreground leading-relaxed">
              Our team has worked with companies ranging from early-stage startups to Fortune 500 enterprises, and we've seen firsthand how AI search is reshaping the digital landscape. We're here to make sure you're not left behind.
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-display font-bold mb-6">
              Our Values
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <Card key={index} className="border-border hover:border-primary/50 transition-colors">
                <CardContent className="pt-6">
                  <div className="w-12 h-12 rounded-lg gradient-primary flex items-center justify-center mb-4">
                    <value.icon className="w-6 h-6 text-primary-foreground" />
                  </div>
                  <h3 className="font-display font-semibold text-lg mb-3">
                    {value.title}
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    {value.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge className="mb-6 bg-primary/10 text-primary border-primary/20">
            Certified Experts
          </Badge>
          <h2 className="text-3xl sm:text-4xl font-display font-bold mb-6">
            Trusted AI Expertise
          </h2>
          <p className="text-lg text-muted-foreground mb-8">
            Our team members hold certifications in AI from IBM, Google, Microsoft, and other leading technology companies. We combine this formal training with real-world experience across diverse industries and markets.
          </p>
          <div className="flex flex-wrap justify-center items-center gap-8 opacity-80">
            <img
              src="/assets/ibm.png"
              alt="IBM"
              className="h-10 w-auto object-contain"
              loading="lazy"
            />
            <img
              src="/assets/google.webp"
              alt="Google"
              className="h-10 w-auto object-contain"
              loading="lazy"
            />
            <img
              src="/assets/microsoft.webp"
              alt="Microsoft"
              className="h-10 w-auto object-contain"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-display font-bold mb-6">
              Meet the Team
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Built by people who understand AI visibility, automation, and revenue growth
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-5xl mx-auto">
            {team.map((member) => (
              <Card key={member.name} className="border-border text-center">
                <CardContent className="pt-6 flex flex-col items-center">
                  <div className="w-20 h-20 rounded-xl bg-muted flex items-center justify-center mb-4 text-lg font-display text-muted-foreground">
                    {member.name.charAt(0)}
                  </div>
                  <h3 className="font-display font-semibold text-xl mb-1">
                    {member.name}
                  </h3>
                  <p className="text-sm text-primary mb-3">{member.role}</p>
                  <p className="text-sm text-muted-foreground">{member.bio}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-display font-bold mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-muted-foreground">
              A few quick answers about Evolution GEO and how we work.
            </p>
          </div>

          <div className="space-y-6">
            <div className="border border-border rounded-xl p-6 bg-card/40">
              <h3 className="font-display font-semibold text-lg mb-2">
                What kind of companies do you typically work with?
              </h3>
              <p className="text-muted-foreground">
                We usually partner with B2B SaaS, ecommerce, and service businesses that already have some traction
                and want to turn AI search and AI agents into a predictable acquisition channel. If you have a clear
                offer and existing demand, we can usually help.
              </p>
            </div>

            <div className="border border-border rounded-xl p-6 bg-card/40">
              <h3 className="font-display font-semibold text-lg mb-2">
                How is GEO/AIO different from traditional SEO?
              </h3>
              <p className="text-muted-foreground">
                Traditional SEO focuses on ranking in search results pages. GEO/AIO focuses on making sure AI engines
                like ChatGPT, Perplexity, and Google&apos;s AI experiences can understand, trust, and reference your brand
                when people ask for recommendations or solutions.
              </p>
            </div>

            <div className="border border-border rounded-xl p-6 bg-card/40">
              <h3 className="font-display font-semibold text-lg mb-2">
                What happens after I request a free GEO/AIO audit?
              </h3>
              <p className="text-muted-foreground">
                We manually review how your brand shows up across key AI engines, run supporting checks, and prepare
                a simple, clear report with opportunities and next steps. You&apos;ll receive it by email, and you can
                decide whether you want us to help implement the recommendations.
              </p>
            </div>

            <div className="border border-border rounded-xl p-6 bg-card/40">
              <h3 className="font-display font-semibold text-lg mb-2">
                Do you offer ongoing retainers or one-off projects?
              </h3>
              <p className="text-muted-foreground">
                Most clients work with us on a monthly retainer for GEO/AIO, AI ads, and automations, so we can keep
                improving visibility and revenue over time. In some cases we also take on focused projects if there is
                a clear, well-scoped outcome.
              </p>
            </div>

            <div className="border border-border rounded-xl p-6 bg-card/40">
              <h3 className="font-display font-semibold text-lg mb-2">
                How long does it take to see results from GEO/AIO?
              </h3>
              <p className="text-muted-foreground">
                Timelines depend on your starting point, category, and competition. Many clients start seeing early
                movement in AI citations and recommendations within 8–12 weeks, with more meaningful shifts over the
                following quarters as we keep reinforcing signals.
              </p>
            </div>

            <div className="border border-border rounded-xl p-6 bg-card/40">
              <h3 className="font-display font-semibold text-lg mb-2">
                Do you replace our internal team or work alongside them?
              </h3>
              <p className="text-muted-foreground">
                We usually plug in alongside your internal marketing, content, or growth team. We bring GEO/AIO and AI
                expertise, you bring brand and domain knowledge—together we build a system that fits how you already work.
              </p>
            </div>

            <div className="border border-border rounded-xl p-6 bg-card/40">
              <h3 className="font-display font-semibold text-lg mb-2">
                Which tools and platforms do you typically integrate with?
              </h3>
              <p className="text-muted-foreground">
                We regularly integrate with Webflow, WordPress, Shopify, Framer, React and Next.js sites, plus popular
                CRMs, analytics tools, and marketing platforms. Our goal is to fit into your existing stack, not replace it.
              </p>
            </div>

            <div className="border border-border rounded-xl p-6 bg-card/40">
              <h3 className="font-display font-semibold text-lg mb-2">
                Can you help us with AI ads and AI agents only?
              </h3>
              <p className="text-muted-foreground">
                Yes. Some clients come to us primarily for AI-native ad strategy or AI agents and automations. We&apos;ll
                still look at your GEO/AIO foundations, but we can scope an engagement that focuses on the areas you need most.
              </p>
            </div>

            <div className="border border-border rounded-xl p-6 bg-card/40">
              <h3 className="font-display font-semibold text-lg mb-2">
                How do you measure success for GEO/AIO work?
              </h3>
              <p className="text-muted-foreground">
                We look at shifts in AI citations and recommendations, qualified traffic and leads from AI-influenced journeys,
                and ultimately pipeline and revenue impact. We&apos;ll align on the right success metrics with you at the start.
              </p>
            </div>

            <div className="border border-border rounded-xl p-6 bg-card/40">
              <h3 className="font-display font-semibold text-lg mb-2">
                How do we get started working with Evolution GEO?
              </h3>
              <p className="text-muted-foreground">
                The easiest way to start is with a free GEO/AIO audit or a short discovery call. From there, we&apos;ll
                align on goals, scope, and the best engagement model, then map out a clear 90-day plan together.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-display font-bold mb-6">
            Work With Us
          </h2>
          <p className="text-lg text-muted-foreground mb-8">
            Ready to make your brand the default answer in AI search? Start with a free audit.
          </p>
          <Link to="/free-audit">
            <Button size="lg" className="gradient-primary text-primary-foreground shadow-primary">
              Get My Free GEO/AIO Audit <span className="font-semibold">(Valued at $2,000+)</span>
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};
